import { ActionList, Text } from "@certn/cascadia";
import { CheckRuleSetActionListItem } from "./CheckRuleSetActionListItem";
import { EmploymentVerifAdjudicationCriteria } from "./EmploymentVerifAdjudicationCriteria";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { CCRCAdjudicationCriteria } from "./canadianCriminalRecord/CCRCAdjudicationCriteria";
import { SubCategory } from "../types/canadian-criminal-record-types";
import { getCheckTypeName } from "../utils";

type SetStatusValuesType = {
  criteria: {
    check_type: string;
    ruleset: Record<string, unknown>;
    set?: boolean;
  }[];
};

export function CreateEditAdjudicationFlow({
  checkTypes,
  selectedCheckType,
  onNavigationChange,
  selectedSubCategory,
  onSubCategorySelect,
}: {
  checkTypes?: {
    id: string;
    name: string;
  }[];
  selectedCheckType: string | null;
  onNavigationChange: (selectedCheckType: string | null) => void;
  selectedSubCategory: SubCategory | null;
  onSubCategorySelect: (subCategory: SubCategory) => void;
}) {
  const { t } = useTranslation();
  if (!checkTypes) {
    checkTypes = [
      {
        id: "EMPLOYMENT_VERIFICATIONS_1",
        name: getCheckTypeName("EMPLOYMENT_VERIFICATIONS_1", t),
      },
      {
        id: "BASIC_CANADIAN_CRIMINAL_RECORD_REPORT_1",
        name: getCheckTypeName("BASIC_CANADIAN_CRIMINAL_RECORD_REPORT_1", t),
      },
    ];
  }

  const { values } = useFormikContext<{
    criteria: any[];
  }>();

  const getSetStatus = (check_type: string, values: SetStatusValuesType) => {
    const checkCriteria = values.criteria.find(
      (criteria) => criteria.check_type === check_type
    );

    if (!checkCriteria) return "NOT_SET";

    return checkCriteria?.set ? "SET" : "NOT_SET";
  };

  // Map check types to their corresponding criteria components
  const criteriaComponents: Record<string, ReactNode> = {
    EMPLOYMENT_VERIFICATIONS_1: (
      <EmploymentVerifAdjudicationCriteria editMode={true} />
    ),
    BASIC_CANADIAN_CRIMINAL_RECORD_REPORT_1: (
      <CCRCAdjudicationCriteria
        selectedSubCategory={selectedSubCategory}
        onSubCategorySelect={onSubCategorySelect}
        editMode={true}
      />
    ),
  };

  // If a check type is selected, show its criteria component
  if (selectedCheckType) {
    return (
      <>
        {criteriaComponents[selectedCheckType] || (
          <Text style="global/body-primary">
            {t("No criteria available for this check type")}
          </Text>
        )}
      </>
    );
  }

  // Otherwise, show the list of check types
  return (
    <ActionList>
      {checkTypes.map((check) => (
        <CheckRuleSetActionListItem
          title={check.name}
          tag={getSetStatus(check.id, values)}
          key={check.id}
          onPress={() => onNavigationChange(check.id)}
        />
      ))}
    </ActionList>
  );
}
